import React, { useState } from "react";
import { useT, useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import {
    FD_REGULAR,
    FD_SENIOR,
    MIP_REGULAR,
    MIP_SENIOR,
    RD_PLAN,
    BAL_VIKAS,
    DAILY_SAVINGS,
    SHORT_TERM_FD,
    SPECIAL_SCHEMES,
    fmtINR
} from "../data/schemes";
import { TID } from "../constants/testIds";

const Tab = ({ active, onClick, testid, children }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`px-4 sm:px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
            active
                ? "bg-red-700 text-white shadow"
                : "bg-white text-slate-700 hover:bg-amber-50 border border-slate-200"
        }`}
    >
        {children}
    </button>
);

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h3>
        {subtitle && <p className="mt-1 text-slate-600 text-sm">{subtitle}</p>}
    </div>
);

const InterestTable = ({ scheme, formatter = fmtINR }) => {
    const tr = useT();
    return (
        <div className="overflow-x-auto table-scroll bg-white rounded-xl border border-slate-200 shadow-sm">
            <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-red-700 to-red-800 text-white">
                    <tr>
                        <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">
                            {tr({ en: "Deposit Amount", mr: "ठेव रक्कम" })}
                        </th>
                        {scheme.columns.map((c) => (
                            <th key={c.key} className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-xs">
                                {c.label}
                                {c.rate && <div className="text-amber-200 normal-case font-bold text-[11px]">{c.rate}% PA</div>}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {scheme.rows.map((r, idx) => (
                        <tr key={idx} className={idx % 2 ? "bg-slate-50" : "bg-white"}>
                            <td className="px-4 py-3 font-bold text-slate-900">₹ {formatter(r.amount)}</td>
                            {scheme.columns.map((c) => (
                                <td key={c.key} className="px-4 py-3 text-right text-slate-700 font-semibold tabular-nums">
                                    ₹ {formatter(r[c.key])}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const RdTable = () => {
    const tr = useT();
    return (
        <div className="overflow-x-auto table-scroll bg-white rounded-xl border border-slate-200 shadow-sm">
            <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-red-700 to-red-800 text-white">
                    <tr>
                        <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">
                            {tr({ en: "Monthly", mr: "मासिक" })}
                        </th>
                        {RD_PLAN.columns.map((c) => (
                            <th key={c.key} className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-xs">
                                {c.label}
                                <div className="text-amber-200 normal-case font-normal text-[10px]">
                                    {tr({ en: "Deposit / Value", mr: "जमा / परिपक्व" })}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {RD_PLAN.rows.map((r, idx) => (
                        <tr key={idx} className={idx % 2 ? "bg-slate-50" : "bg-white"}>
                            <td className="px-4 py-3 font-bold text-slate-900">₹ {fmtINR(r.monthly)}</td>
                            {RD_PLAN.columns.map((c) => (
                                <td key={c.key} className="px-4 py-3 text-right text-slate-700 tabular-nums">
                                    <div className="font-medium text-slate-600 text-xs">₹ {fmtINR(r[c.key].deposit)}</div>
                                    <div className="font-bold text-red-700">₹ {fmtINR(r[c.key].value)}</div>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Premature = ({ list }) => {
    const tr = useT();
    if (!list) return null;
    return (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="font-bold text-amber-800 mb-2 inline-flex items-center gap-2">
                <i className="fa-solid fa-triangle-exclamation" />
                {tr({ en: "Premature Withdrawal Terms", mr: "अकाली पैसे काढण्याच्या अटी" })}
            </div>
            <ul className="space-y-1.5 text-sm text-amber-900">
                {tr(list).map((item, i) => (
                    <li key={i} className="flex gap-2">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const Deposits = () => {
    const tr = useT();
    const { lang } = useLang();
    const [tab, setTab] = useState("fd");

    const tabs = [
        { key: "fd", label: tr(t.deposits.tabs.fd), tid: TID.depositTabFd },
        { key: "fdSenior", label: tr(t.deposits.tabs.fdSenior), tid: TID.depositTabFdSenior },
        { key: "mip", label: tr(t.deposits.tabs.mip), tid: TID.depositTabMip },
        { key: "mipSenior", label: tr(t.deposits.tabs.mipSenior), tid: TID.depositTabMipSenior },
        { key: "rd", label: tr(t.deposits.tabs.rd), tid: TID.depositTabRd },
        { key: "special", label: tr(t.deposits.tabs.special), tid: TID.depositTabSpecial }
    ];

    return (
        <div>
            <section className="bg-gradient-to-br from-slate-900 to-red-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                        {tr(t.deposits.title)}
                    </h1>
                    <p className="mt-4 text-slate-300 max-w-2xl text-base sm:text-lg">{tr(t.deposits.subtitle)}</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-8 scrollbar-thin">
                    {tabs.map((tb) => (
                        <Tab key={tb.key} active={tab === tb.key} onClick={() => setTab(tb.key)} testid={tb.tid}>
                            {tb.label}
                        </Tab>
                    ))}
                </div>

                {/* FD */}
                {tab === "fd" && (
                    <div>
                        <SectionTitle title={tr(FD_REGULAR.title)} subtitle={tr(FD_REGULAR.terms)} />
                        <InterestTable scheme={FD_REGULAR} />
                        <Premature list={FD_REGULAR.premature} />
                    </div>
                )}

                {tab === "fdSenior" && (
                    <div>
                        <SectionTitle title={tr(FD_SENIOR.title)} subtitle={tr(FD_SENIOR.terms)} />
                        <InterestTable scheme={FD_SENIOR} />
                    </div>
                )}

                {tab === "mip" && (
                    <div>
                        <SectionTitle title={tr(MIP_REGULAR.title)} subtitle={tr(MIP_REGULAR.terms)} />
                        <p className="text-xs text-slate-500 mb-3">
                            <i className="fa-solid fa-circle-info mr-1 text-amber-600" />
                            {lang === "mr"
                                ? "खालील आकडे प्रत्येक ठेवीवर मिळणारे मासिक उत्पन्न दर्शवतात (₹ मध्ये)."
                                : "Numbers below indicate monthly income (₹) per deposit slab."}
                        </p>
                        <InterestTable scheme={MIP_REGULAR} />
                        <Premature list={MIP_REGULAR.premature} />
                    </div>
                )}

                {tab === "mipSenior" && (
                    <div>
                        <SectionTitle title={tr(MIP_SENIOR.title)} subtitle={tr(MIP_SENIOR.terms)} />
                        <p className="text-xs text-slate-500 mb-3">
                            <i className="fa-solid fa-circle-info mr-1 text-amber-600" />
                            {lang === "mr" ? "मासिक उत्पन्न (₹ मध्ये)." : "Monthly income figures in ₹."}
                        </p>
                        <InterestTable scheme={MIP_SENIOR} />
                    </div>
                )}

                {tab === "rd" && (
                    <div>
                        <SectionTitle title={tr(RD_PLAN.title)} subtitle={tr(RD_PLAN.terms)} />
                        <RdTable />
                    </div>
                )}

                {tab === "special" && (
                    <div className="space-y-10">
                        <div>
                            <SectionTitle
                                title={tr({ en: "Special Schemes", mr: "विशेष योजना" })}
                                subtitle={tr({ en: "Curated plans for milestones.", mr: "टप्प्यांसाठी विशेष योजना." })}
                            />
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {SPECIAL_SCHEMES.map((s, i) => (
                                    <div
                                        key={i}
                                        className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-amber-500/50 hover:-translate-y-0.5 transition-all"
                                    >
                                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-700 to-amber-600 text-white flex items-center justify-center mb-4">
                                            <i className={`fa-solid ${s.icon} text-xl`} />
                                        </div>
                                        <h4 className="font-bold text-slate-900 mb-1.5 text-[15px]">{tr(s.title)}</h4>
                                        <p className="text-sm text-slate-600 leading-relaxed">{tr(s.desc)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Bal Vikas */}
                        <div>
                            <SectionTitle title={tr(BAL_VIKAS.title)} />
                            <div className="overflow-x-auto table-scroll bg-white rounded-xl border border-slate-200 shadow-sm">
                                <table className="w-full text-sm">
                                    <thead className="bg-gradient-to-r from-amber-600 to-amber-700 text-white">
                                        <tr>
                                            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">
                                                {tr({ en: "Monthly Deposit", mr: "मासिक ठेव" })}
                                            </th>
                                            <th className="text-right px-4 py-3 font-semibold uppercase tracking-wider text-xs">
                                                {tr({ en: "Maturity (5 yr)", mr: "परिपक्वता (५ वर्ष)" })}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {BAL_VIKAS.rows.map((r, i) => (
                                            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                                                <td className="px-4 py-3 font-bold text-slate-900">₹ {fmtINR(r.monthly)}</td>
                                                <td className="px-4 py-3 text-right font-semibold text-red-700 tabular-nums">₹ {fmtINR(r.maturity)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Daily Savings */}
                        <div>
                            <SectionTitle title={tr(DAILY_SAVINGS.title)} />
                            <div className="overflow-x-auto table-scroll bg-white rounded-xl border border-slate-200 shadow-sm">
                                <table className="w-full text-sm">
                                    <thead className="bg-gradient-to-r from-amber-600 to-amber-700 text-white">
                                        <tr>
                                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider">
                                                {tr({ en: "Daily", mr: "दैनिक" })}
                                            </th>
                                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider">1 Year</th>
                                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider">2 Year</th>
                                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider">3 Year</th>
                                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider">
                                                {tr({ en: "Loan Eligibility", mr: "कर्ज पात्रता" })}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {DAILY_SAVINGS.rows.map((r, i) => (
                                            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                                                <td className="px-4 py-3 font-bold text-slate-900">₹ {fmtINR(r.daily)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">₹ {fmtINR(r.y1)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">₹ {fmtINR(r.y2)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">₹ {fmtINR(r.y3)}</td>
                                                <td className="px-4 py-3 text-right font-semibold text-red-700 tabular-nums">₹ {fmtINR(r.loan)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Short term FD */}
                        <div>
                            <SectionTitle title={tr(SHORT_TERM_FD.title)} />
                            <div className="overflow-x-auto table-scroll bg-white rounded-xl border border-slate-200 shadow-sm max-w-2xl">
                                <table className="w-full text-sm">
                                    <thead className="bg-gradient-to-r from-amber-600 to-amber-700 text-white">
                                        <tr>
                                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider">
                                                {tr({ en: "Period", mr: "कालावधी" })}
                                            </th>
                                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider">
                                                {tr({ en: "Rate", mr: "दर" })}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {SHORT_TERM_FD.rows.map((r, i) => (
                                            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                                                <td className="px-4 py-3 font-medium">{tr(r.period)}</td>
                                                <td className="px-4 py-3 text-right font-bold text-red-700">{r.rate}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default Deposits;
